// Board topology helpers for the sea frame (pure, no THREE): ring growth
// around a tile set, edge depth, the landlocked wall transform and the outer
// boundary edges (for the mist curtain). Works on any tile set, so Square /
// Circular / Triangle boards all use the same code.

import type { SceneNode } from '@aod/protocol';
import { EDGE_NEIGHBOURS } from '../terrainEdges.js';
import { wallLevel } from './seaFrame.js';

const SQ3 = Math.sqrt(3);

export const key = (q: number, r: number): string => `${q},${r}`;

/** Axial -> world XZ (pointy-top hexes, circumradius 1) — matches render/hexLayout. */
export function axialXZ(q: number, r: number): [number, number] {
  return [SQ3 * (q + r / 2), 1.5 * r];
}

/** World XZ -> nearest axial hex (cube rounding). */
export function worldToAxial(x: number, z: number): [number, number] {
  const fq = (SQ3 / 3) * x - z / 3;
  const fr = (2 / 3) * z;
  let q = Math.round(fq);
  let r = Math.round(fr);
  const s = Math.round(-fq - fr);
  const dq = Math.abs(q - fq);
  const dr = Math.abs(r - fr);
  const ds = Math.abs(s + fq + fr);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return [q + 0, r + 0]; // + 0 normalises -0
}

interface TileProps {
  q?: number;
  r?: number;
  biome?: string;
  wall?: boolean;
}

/** key -> biome for every tile node that carries q,r. */
export function boardBiomes(tiles: ReadonlyArray<SceneNode>): Map<string, string> {
  const m = new Map<string, string>();
  for (const t of tiles) {
    const p = (t.props ?? {}) as TileProps;
    if (p.q === undefined || p.r === undefined) continue;
    m.set(key(p.q, p.r), p.biome ?? 'plains');
  }
  return m;
}

export function parseKey(k: string): [number, number] {
  const i = k.indexOf(',');
  return [Number(k.slice(0, i)), Number(k.slice(i + 1))];
}

export interface RingTile {
  readonly q: number;
  readonly r: number;
  /** 1 = touching the board, `rings` = outermost. */
  readonly depth: number;
}

/**
 * `rings` rings of tiles outside `board`, nearest first; inside a ring sorted
 * by (q, r) so the result is deterministic regardless of Map order.
 */
export function outwardRings(board: ReadonlySet<string>, rings: number): RingTile[] {
  const seen = new Set(board);
  let frontier: string[] = [...board];
  const out: RingTile[] = [];
  for (let depth = 1; depth <= rings; depth++) {
    const next = new Set<string>();
    for (const k of frontier) {
      const [q, r] = parseKey(k);
      for (const [dq, dr] of EDGE_NEIGHBOURS) {
        const nk = key(q + dq, r + dr);
        if (!seen.has(nk)) next.add(nk);
      }
    }
    for (const k of next) seen.add(k);
    const list = [...next].map(parseKey).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    for (const [q, r] of list) out.push({ q, r, depth });
    frontier = [...next];
  }
  return out;
}

/** key -> distance from the board edge (0 = has a missing neighbour). */
export function edgeDepths(board: ReadonlySet<string>): Map<string, number> {
  const depth = new Map<string, number>();
  let frontier: string[] = [];
  for (const k of board) {
    const [q, r] = parseKey(k);
    if (EDGE_NEIGHBOURS.some(([dq, dr]) => !board.has(key(q + dq, r + dr)))) {
      depth.set(k, 0);
      frontier.push(k);
    }
  }
  for (let d = 1; frontier.length > 0; d++) {
    const next: string[] = [];
    for (const k of frontier) {
      const [q, r] = parseKey(k);
      for (const [dq, dr] of EDGE_NEIGHBOURS) {
        const nk = key(q + dq, r + dr);
        if (board.has(nk) && !depth.has(nk)) {
          depth.set(nk, d);
          next.push(nk);
        }
      }
    }
    frontier = next;
  }
  return depth;
}

/**
 * Landlocked preview transform: the outer `wallRings` rings of the board
 * become mountain with pinned levels (12 / 11) and a `wall: true` prop.
 * Interior tiles are returned untouched (same object); wall tiles lose river
 * and volcano props. Returns the SAME array when there is nothing to do.
 */
export function applyLandlockedWall(
  tiles: ReadonlyArray<SceneNode>,
  wallRings = 2,
): ReadonlyArray<SceneNode> {
  if (wallRings <= 0 || tiles.length === 0) return tiles;
  const depths = edgeDepths(new Set(boardBiomes(tiles).keys()));
  return tiles.map((t) => {
    const p = (t.props ?? {}) as TileProps;
    if (p.q === undefined || p.r === undefined) return t;
    const d = depths.get(key(p.q, p.r));
    if (d === undefined || d >= wallRings) return t;
    const {
      riverEdges: _re,
      bridgeEdges: _be,
      volcano: _v,
      ...rest
    } = t.props as Record<string, unknown>;
    return { ...t, props: { ...rest, biome: 'mountain', level: wallLevel(d), wall: true } };
  });
}

/** key -> wall row for the tiles `applyLandlockedWall` turns into wall. */
export function wallRows(tiles: ReadonlyArray<SceneNode>, wallRings = 2): Map<string, number> {
  const depths = edgeDepths(new Set(boardBiomes(tiles).keys()));
  const out = new Map<string, number>();
  for (const [k, d] of depths) if (d < wallRings) out.set(k, d);
  return out;
}

export interface BoundaryEdge {
  /** Edge midpoint, world XZ. */
  readonly x: number;
  readonly z: number;
  /** Unit outward normal. */
  readonly nx: number;
  readonly nz: number;
}

/** Outer boundary edges of the tile set (a hex side has length 1). */
export function boundaryEdges(board: ReadonlySet<string>): BoundaryEdge[] {
  const out: BoundaryEdge[] = [];
  const sorted = [...board].map(parseKey).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  for (const [q, r] of sorted) {
    const [cx, cz] = axialXZ(q, r);
    for (const [dq, dr] of EDGE_NEIGHBOURS) {
      if (board.has(key(q + dq, r + dr))) continue;
      const [vx, vz] = axialXZ(dq, dr); // centre-to-centre vector, length sqrt(3)
      const len = Math.hypot(vx, vz);
      out.push({ x: cx + vx / 2, z: cz + vz / 2, nx: vx / len, nz: vz / len });
    }
  }
  return out;
}

/** Cheap board signature: changes only when coordinates or biomes change. */
export function terrainSignature(tiles: ReadonlyArray<SceneNode>): string {
  let h = 2166136261;
  let n = 0;
  for (const t of tiles) {
    const p = (t.props ?? {}) as TileProps;
    if (p.q === undefined || p.r === undefined) continue;
    n++;
    const b = p.biome ?? '';
    h = Math.imul(h ^ (p.q + 1000), 16777619);
    h = Math.imul(h ^ (p.r + 1000), 16777619);
    for (let i = 0; i < b.length; i++) h = Math.imul(h ^ b.charCodeAt(i), 16777619);
    if (p.wall) h = Math.imul(h ^ 77, 16777619);
  }
  return `${n}:${(h >>> 0).toString(36)}`;
}

/** Unsigned 32-bit seed derived from a signature string. */
export function seedOf(sig: string): number {
  let h = 2166136261;
  for (let i = 0; i < sig.length; i++) h = Math.imul(h ^ sig.charCodeAt(i), 16777619);
  return h >>> 0;
}
